/**
 * A revelação SUAVE do texto da bolha (11/09/2026) — o mixin que liga a
 * `RevelacaoDeTexto` (`utils/revelacao-de-texto.js`) ao `chat-message.vue`.
 *
 * A bolha guarda o que já CHEGOU (`content`, tokens do store ou o texto da
 * mensagem) e mostra só um prefixo dele (`textoExibido`), que um laço de
 * `requestAnimationFrame` faz crescer a uma taxa constante — o texto "digita"
 * em vez de entrar aos blocos. O que o renderer recebe é o prefixo com a
 * ênfase aberta fechada (`fecharMarkdownAberto`), para o negrito não piscar.
 *
 * ## O fecho atravessa a troca de bolha
 *
 * No `end`, `messages.vue` DESTRÓI a bolha em streaming e MONTA a persistida
 * (`addMessage` + `chatLoading = false`, no mesmo tick). Se a tela estava
 * atrasada em relação ao recebido, a persistida mostraria o texto inteiro de
 * uma vez — um salto no exato instante em que o turno termina. Por isso a
 * bolha em streaming emite `revelado` com o prefixo a cada avanço, o
 * `messages.vue` guarda o último (`textoReveladoDoTurno`) e o entrega à bolha
 * seguinte pela prop `textoReveladoInicial`; a persistida, se for a mensagem
 * que acabou de nascer (`isLast` + `duracao_s`, que só a mensagem criada AO
 * VIVO tem — `apiChat._onEventEnd`), retoma do prefixo e completa em
 * `MS_DO_FECHO`. Depois emite `revelacao-concluida`, e o pai zera o prefixo.
 *
 * ⚠️ O prefixo tem de ser PREFIXO do `content` persistido (`deveFecharRevelacao`).
 * O `end` carrega `response`, que hoje é a concatenação dos tokens — mas se
 * um dia divergir, o fecho é dispensado e o texto entra inteiro: um salto é
 * melhor que mostrar um texto que não é o da mensagem.
 *
 * ## O texto que chega num BLOCO SÓ revela do zero
 *
 * ★ [owner, 21/09/2026] No caminho do Supervisor o backend NÃO manda `token`:
 * o texto inteiro vem num único `text-delta` (`apiChat.js::
 * _onDeltaDeTextoSombra`) e o `end` logo atrás. A bolha em streaming nunca
 * chega a revelar nada, o bastão vem VAZIO, e a persistida entrava com o texto
 * inteiro — *"apareceu do nada, bem brusco"*. Hoje o prefixo vazio é um
 * prefixo como outro qualquer: a mensagem que acabou de nascer revela do
 * zero, no ritmo do bloco (`TAXA_MAXIMA_DO_BLOCO_CHARS_POR_MS`, ~600 chars/s),
 * com o fade por palavra e a pílula "↓ Novas linhas" de sempre.
 *
 * Fora disto tudo (mensagem do usuário, histórico reaberto,
 * `prefers-reduced-motion`, aba oculta no fecho) `revelacaoAtiva` fica `false`
 * e a bolha renderiza `content` como sempre renderizou.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila).
 */
import {
  RevelacaoDeTexto,
  TAXA_MAXIMA_DO_BLOCO_CHARS_POR_MS,
  fecharMarkdownAberto,
  prefereMenosMovimento,
} from '@modules/aawz-intelligence-v2/utils/revelacao-de-texto'
import {
  DURACAO_DO_ACENDER_MS,
  acenderUltimasPalavras,
  contarPalavras,
} from '@modules/aawz-intelligence-v2/utils/acender-palavras'

export const EVENTO_REVELADO = 'revelado'
export const EVENTO_REVELACAO_CONCLUIDA = 'revelacao-concluida'
/** Teto do rastro de carimbos: mais do que isso já acendeu há tempo. */
const PALAVRAS_NO_RASTRO = 64

/**
 * A bolha persistida deve RETOMAR a revelação de onde a bolha em streaming
 * parou? Só a mensagem que acabou de nascer do turno, e só se o prefixo é
 * mesmo o começo do texto dela. Prefixo VAZIO (nada foi streamado — o bloco
 * do Supervisor) retoma do zero: é o começo de qualquer texto.
 *
 * @param {Object}  opcoes
 * @param {string}  opcoes.prefixo       O que a bolha em streaming chegou a mostrar.
 * @param {string}  opcoes.conteudo      O `content` da mensagem persistida.
 * @param {boolean} opcoes.isLast        É a última bolha do thread.
 * @param {boolean} opcoes.criadaAoVivo  Tem `duracao_s` — nasceu do `end`, não do histórico.
 */
export function deveFecharRevelacao({ prefixo, conteudo, isLast, criadaAoVivo }) {
  if (!isLast || !criadaAoVivo) return false
  if (!conteudo) return false
  const inicio = prefixo || ''
  if (inicio.length >= conteudo.length) return false
  return conteudo.indexOf(inicio) === 0
}

// Com a aba oculta o `requestAnimationFrame` não roda: o fecho ficaria preso
// no prefixo até o usuário voltar. Ali o texto entra inteiro.
function documentoOculto() {
  return typeof document !== 'undefined' && document.hidden === true
}

export const revelacaoDoTexto = {
  props: {
    // O prefixo que a bolha em streaming chegou a mostrar — ver o cabeçalho.
    textoReveladoInicial: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      revelacaoAtiva: false,
      textoExibidoLen: 0,
    }
  },
  computed: {
    // O prefixo de `content` que está na tela. Fora da revelação, o texto todo.
    textoExibido() {
      const alvo = this.content || ''
      if (!this.revelacaoAtiva) return alvo
      return alvo.slice(0, this.textoExibidoLen)
    },
    // O que vai ao renderer: durante a revelação, com a ênfase aberta fechada.
    textoParaRenderizar() {
      if (!this.revelacaoAtiva) return this.textoExibido
      return fecharMarkdownAberto(this.textoExibido)
    },
  },
  watch: {
    content(novo, antigo) {
      if (this._revelacao) return this._revelacao.receber(novo || '')
      // ★ [owner, 21/09/2026] O texto que chega DEPOIS de a bolha nascer: o
      // atalho (`/quem-migrar`, `/panorama`) segura o `content` em '' enquanto
      // a encenação do plano anda (`aguardandoEncenacaoDoPlano`), e a bolha
      // persistida nascia sem laço nenhum — ao soltar, o texto entrava
      // inteiro, sem revelação e com os cartões atrás dele no mesmo quadro.
      // Nascendo vazio e enchendo agora, arma como se estivesse nascendo.
      if (!antigo && novo) this.iniciarRevelacao()
    },
  },
  created() {
    this.iniciarRevelacao()
  },
  beforeDestroy() {
    this.pararRevelacao()
  },
  methods: {
    iniciarRevelacao() {
      if (!this.isAssistant || prefereMenosMovimento()) return
      if (this.isStreaming) {
        this.armarRevelacao(0, false)
        return
      }
      if (documentoOculto()) return
      const prefixo = this.textoReveladoInicial || ''
      const retomar = deveFecharRevelacao({
        prefixo: prefixo,
        conteudo: this.content,
        isLast: this.isLast,
        criadaAoVivo: this.duracaoDoTurno !== null,
      })
      if (retomar) this.armarRevelacao(prefixo.length, true)
    },
    armarRevelacao(exibidoLen, fecho) {
      const self = this
      // Fora do `data()` de propósito: é um handle de laço, não estado de tela.
      // `_palavrasNovas`: o instante em que cada palavra apareceu (as últimas
      // só) — é a idade delas que dá o `animation-delay` do fade.
      this._palavrasNovas = []
      this._revelacao = new RevelacaoDeTexto({
        exibidoLen: exibidoLen,
        aoMudar(len) {
          self.registrarPalavrasNovas(self.textoExibidoLen, len)
          self.textoExibidoLen = len
          self.$emit(EVENTO_REVELADO, self.textoExibido)
          self.$nextTick(self.acenderPalavrasNovas)
        },
        aoConcluir() {
          self.concluirRevelacao()
        },
      })
      this.textoExibidoLen = exibidoLen
      this.revelacaoAtiva = true
      this._revelacao.receber(this.content || '')
      if (!fecho) return
      // Fecho do zero = nada foi streamado: é o bloco, no teto dele.
      const bloco = exibidoLen === 0
      this._revelacao.fechar(bloco ? { teto: TAXA_MAXIMA_DO_BLOCO_CHARS_POR_MS } : undefined)
    },
    // Só o fecho conclui; a bolha em streaming é destruída no `end`.
    concluirRevelacao() {
      this.pararRevelacao()
      this.revelacaoAtiva = false
      this.$emit(EVENTO_REVELACAO_CONCLUIDA)
      // Os botões de copiar (tabela/código) esperavam o texto inteiro.
      if (typeof this.injetarAcoesMarkdown === 'function') {
        this.$nextTick(this.injetarAcoesMarkdown)
      }
    },
    pararRevelacao() {
      if (!this._revelacao) return
      this._revelacao.parar()
      this._revelacao = null
    },
    /**
     * ★ [owner, 21/09/2026] O fade por palavra. A cada avanço da revelação,
     * as palavras que entraram (`de` → `ate`, cortadas em palavra pela própria
     * revelação) ganham um carimbo de tempo; depois do render, as que têm
     * menos de `DURACAO_DO_ACENDER_MS` são embrulhadas com o `delay` negativo
     * da idade (`utils/acender-palavras.js`) — o fade continua de onde estava
     * mesmo com o markdown trocado por baixo.
     */
    registrarPalavrasNovas(de, ate) {
      const alvo = this.content || ''
      const novas = contarPalavras(alvo.slice(de, ate))
      if (!novas) return
      const agora = Date.now()
      const lista = this._palavrasNovas || []
      for (let i = 0; i < novas; i += 1) lista.push(agora)
      // Só o rastro recente interessa: o que já acendeu não volta a acender.
      this._palavrasNovas = lista.slice(-PALAVRAS_NO_RASTRO)
    },
    acenderPalavrasNovas() {
      const lista = this._palavrasNovas || []
      if (!lista.length || !this.$el || !this.$el.querySelector) return
      const agora = Date.now()
      const idades = lista
        .map((em) => agora - em)
        .filter((idade) => idade < DURACAO_DO_ACENDER_MS)
      if (!idades.length) return
      const container = this.$el.querySelector('.ia-message-content .markdown-style')
      if (container) acenderUltimasPalavras(container, idades)
    },
  },
}

export default revelacaoDoTexto
